import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import BlendedService from "../Service/BlendedService";

/**
 * UpdateBlendedCourse component to manage and update blended course information.
 * @returns {JSX.Element} The UpdateBlendedCourse component.
 */
function UpdateBlendedCourse() {
  const navigate = useHistory();
  const { blendedProgramId } = useParams();
  const location = useLocation();
  const { user } = location.state || {}; // Extract user from location.state

  const [blendedProgramName, setBlendedProgramName] = useState("");
  const [programWDCourseCode, setProgramWDCourseCode] = useState("");
  const [modules, setModules] = useState([]);
  const [rowCounter, setRowCounter] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlendedCourseData(blendedProgramId);
  }, [blendedProgramId]);

  /**
   * Fetches blended course data by ID from the server.
   * @param {string} blendedProgramId - The ID of the course to fetch data for.
   */
  const fetchBlendedCourseData = async (blendedProgramId) => {
    try {
      setLoading(true);
      const response = await BlendedService.getCourseById(blendedProgramId);
      
      setBlendedProgramName(response.blendedProgramName || "");
      setProgramWDCourseCode(response.programWDCourseCode === "N/A" ? "" : response.programWDCourseCode || "");
      
      // Transform modules data to match the structure used in AddBlendedCourse
      if (response.modules && response.modules.length > 0) {
        const transformedModules = response.modules.map((module, index) => ({
          id: index + 1,
          moduleId: module.moduleId || "", // Preserve backend auto-generated ID
          moduleName: module.moduleName || "",
          moduleWDCourseCode: module.moduleWDCourseCode || "",
          description: module.description || "",
          duration: module.duration || "",
          numOfSMEConnects: module.numOfSMEConnects || "",
          track: user?.track || response.track || "",
        }));
        setModules(transformedModules);
        setRowCounter(transformedModules.length + 1);
      } else {
        // Default module if no modules exist
        setModules([{
          id: 1,
          moduleId: "", // No ID for new modules
          moduleName: "",
          moduleWDCourseCode: "",
          description: "",
          duration: "",
          numOfSMEConnects: "",
          track: user?.track || response.track || "",
        }]);
        setRowCounter(2);
      }
    } catch (error) {
      console.error("Error fetching blended course data:", error);
      alert("Failed to load course data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Validation functions with alert boxes (same as AddBlendedCourse)
  const validateField = (fieldName, value, rowId = null) => {
    let errorMessage = "";

    switch (fieldName) {
      case "Program Name":
        if (!value || value.trim() === "") {
          errorMessage = "Program name is required";
        } else if (value.trim().length < 3) {
          errorMessage = "Program name must be at least 3 characters long";
        }
        break;

      case "Program WD Course Code":
        if (!value || value.trim() === "") {
          errorMessage = "Module WD Course Code is required";
        } 
        //else if (value.trim().length < 2) {
        //   errorMessage = "Module WD Course Code must be at least 2 characters long";
        // }
        // Program WD Course Code is now optional - no validation needed
        break;
      
      case "Module Name":
        if (!value || value.trim() === "") {
          errorMessage = "Module name is required";
        } else if (value.trim().length < 2) {
          errorMessage = "Module name must be at least 2 characters long";
        }
        break;

      case "Module WD Course Code":
        if (!value || value.trim() === "") {
          errorMessage = "Module WD Course Code is required";
        } else if (value.trim().length < 2) {
          errorMessage = "Module WD Course Code must be at least 2 characters long";
        }
        break;
      
      case "Description":
        if (!value || value.trim() === "") {
          errorMessage = "Description is required";
        } else if (value.trim().length < 10) {
          errorMessage = "Description must be at least 10 characters long";
        }
        break;
      
      case "Duration":
        if (!value || value === "") {
          errorMessage = "Duration is required";
        } else {
          const num = Number(value);
           if (isNaN(num) || num <= 0) {
            errorMessage = "Duration must be greater than 0";
          } else if (num > 100) {
            errorMessage = "Duration cannot exceed 100 hours";
          }
        }
        break;
      
      case "SME Connects":
        if (!value || value === "") {
          errorMessage = "Number of SME connects is required";
        } else {
          const num = Number(value);
          if (isNaN(num) || num <= 0) {
            errorMessage = "Number of SME connects must be greater than 0";
          } else if (num > 50) {
            errorMessage = "Number of SME connects cannot exceed 50";
          }
        }
        break;
    }

    if (errorMessage) {
      alert(errorMessage);
      return false;
    }
    return true;
  };

  const validateAllFields = () => {
    // Validate program name
    if (!validateField("Program Name", blendedProgramName)) {
      return false;
    }

    // Program WD Course Code is optional - no validation needed

    // Validate all modules
    for (let module of modules) {
      if (!validateField("Module Name", module.moduleName, module.id)) return false;
      if (!validateField("Module WD Course Code", module.moduleWDCourseCode, module.id)) return false;
      if (!validateField("Description", module.description, module.id)) return false;
      if (!validateField("Duration", module.duration, module.id)) return false;
      if (!validateField("SME Connects", module.numOfSMEConnects, module.id)) return false;
    }

    return true;
  };

  const handleChange = (id, field, value) => {
    setModules((prev) =>
      prev.map((module) =>
        module.id === id ? { ...module, [field]: value } : module
      )
    );
  };

  const handleProgramNameChange = (value) => {
    setBlendedProgramName(value);
  };

  const handleProgramWDCourseCodeChange = (value) => {
    setProgramWDCourseCode(value);
  };

  const addRow = () => {
    // Validate current modules before adding new one
    for (let module of modules) {
      if (!validateField("Module Name", module.moduleName, module.id)) return;
      if (!validateField("Module WD Course Code", module.moduleWDCourseCode, module.id)) return;
      if (!validateField("Description", module.description, module.id)) return;
      if (!validateField("Duration", module.duration, module.id)) return;
      if (!validateField("SME Connects", module.numOfSMEConnects, module.id)) return;
    }

    setModules((prev) => [
      ...prev,
      {
        id: rowCounter,
        moduleId: "", // No ID for new modules
        moduleName: "",
        moduleWDCourseCode: "",
        description: "",
        duration: "",
        numOfSMEConnects: "",
        track: user?.track || "",
      },
    ]);
    setRowCounter((prev) => prev + 1);
  };

  const removeRow = (id) => {
    if (modules.length > 1) {
      setModules((prev) => prev.filter((module) => module.id !== id));
    } else {
      alert("Cannot remove the last module. At least one module is required.");
    }
  };

  // Check if all fields are filled (for button states)
  const isFormValid = () => {
    if (!blendedProgramName.trim()) return false;
    // programWDCourseCode is optional - no check needed
    
    return modules.every(module => 
      module.moduleName.trim() !== "" &&
      module.moduleWDCourseCode.trim() !== "" &&
      module.description.trim() !== "" &&
      module.duration !== "" &&
      Number(module.duration) > 0 &&
      module.numOfSMEConnects !== "" &&
      Number(module.numOfSMEConnects) >= 0
    );
  };

  /**
   * Handles the form submission to update blended course data.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = async () => {
    // Validate all fields with alert boxes
    if (!validateAllFields()) {
      return; // Validation failed, alerts already shown
    }

    const payload = {
      blendedProgramId: blendedProgramId,
      blendedProgramName: blendedProgramName.trim(),
      programWDCourseCode: programWDCourseCode.trim() || "N/A",
      track: user?.track || "",
      modules: modules.map(module => ({
        ...(module.moduleId && { moduleId: module.moduleId }), // Include moduleId only if it exists
        moduleName: module.moduleName.trim(),
        moduleWDCourseCode: module.moduleWDCourseCode.trim(),
        description: module.description.trim(),
        duration: Number(module.duration),
        numOfSMEConnects: Number(module.numOfSMEConnects),
      })),
    };

    try {
      const confirmUpdate = window.confirm(
        "Are you sure you want to update this blended course?"
      );
      if (confirmUpdate) {
        await BlendedService.updateCourse(payload);
        alert("Blended course updated successfully!");
        navigate.push({
          pathname: "/admin/blended-course-management",
          state: { user }, 
        });
      }
    } catch (error) {
      console.error("Error updating blended course:", error);
      alert("Failed to update blended course: " + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-600 text-lg">Loading course data...</p>
      </div>
    );
  }

  return (
    <div>
      <br/>
      <h2 className="text-2xl w-1/3 mx-auto font-semibold mb-4 text-center">Update Blended Course</h2>
      <br/>
      <div className="w-11/12 mx-auto p-8 bg-white rounded-lg shadow-md">
        {/* Program Name, Course Code and Track Row */}
        <div className="mb-6 flex gap-6">
          {/* Program Name Input */}
          <div className="flex-1">
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Program Name: <span style={{color: 'red'}}>*</span>
            </label>
            <input
              type="text"
              value={blendedProgramName}
              onChange={(e) => handleProgramNameChange(e.target.value)}
              placeholder="Enter program name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Program WD Course Code Input */}
          <div className="flex-1">
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Program WD Course Code: <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <input
              type="text"
              value={programWDCourseCode}
              onChange={(e) => handleProgramWDCourseCodeChange(e.target.value)}
              placeholder="Enter program WD course code (optional)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Track Display */}
          <div className="flex-1">
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Track:
            </label>
            <input
              type="text"
              value={user?.track || ""}
              readOnly
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-600"
              placeholder="Track will be automatically assigned"
            />
          </div>
        </div>

        {/* Modules Table */}
        <div className="mb-6 overflow-x-auto flex justify-center">
          <table className="w-full max-w-5xl border-collapse bg-white border border-gray-300 rounded-lg overflow-hidden shadow-sm">
            <thead>
              <tr className="bg-gray-200 text-gray-600">
                <th className="px-4 py-3 text-left font-semibold border-b">#</th>
                <th className="px-4 py-3 text-left font-semibold border-b">
                  Module Name <span style={{color: '#ffeb3b'}}>*</span>
                </th>
                <th className="px-4 py-3 text-left font-semibold border-b">
                  Module WD Course Code <span style={{color: '#ffeb3b'}}>*</span>
                </th>
                <th className="px-4 py-3 text-left font-semibold border-b">
                  Description <span style={{color: '#ffeb3b'}}>*</span>
                </th>
                <th className="px-4 py-3 text-left font-semibold border-b">
                  Duration (hours) <span style={{color: '#ffeb3b'}}>*</span>
                </th>
                <th className="px-4 py-3 text-left font-semibold border-b">
                  No. of SME Connects <span style={{color: '#ffeb3b'}}>*</span>
                </th>
                <th className="px-4 py-3 text-center font-semibold border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((module) => (
                <tr key={module.id} className="hover:bg-gray-50 border-b border-gray-200">
                  <td className="px-4 py-3 text-center">{module.id}</td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={module.moduleName}
                      onChange={(e) =>
                        handleChange(module.id, "moduleName", e.target.value)
                      }
                      placeholder="Enter module name"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={module.moduleWDCourseCode}
                      onChange={(e) =>
                        handleChange(module.id, "moduleWDCourseCode", e.target.value)
                      }
                      placeholder="Enter module WD course code"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </td>
                  <td className="px-4 py-3">
                    <textarea
                      value={module.description}
                      onChange={(e) =>
                        handleChange(module.id, "description", e.target.value)
                      }
                      placeholder="Enter module description"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px] resize-vertical"
                      required
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={module.duration}
                      onChange={(e) =>
                        handleChange(module.id, "duration", e.target.value)
                      }
                      placeholder="Hours"
                      min="0.1"
                      max="100"
                      step="0.1"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={module.numOfSMEConnects}
                      onChange={(e) =>
                        handleChange(module.id, "numOfSMEConnects", e.target.value)
                      }
                      placeholder="Count"
                      min="0"
                      max="50"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => removeRow(module.id)}
                      disabled={modules.length === 1}
                      className="px-3 py-1 bg-red-500 text-white text-sm font-semibold rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={addRow}
            disabled={!isFormValid()}
            className="px-4 py-23 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            + Add Module
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          >
            Update Course
          </button>
          <button
            type="button"
            onClick={() => navigate.push({
              pathname: "/admin/blended-course-management",
              state: { user }
            })}
            className="px-4 py-2 bg-gray-500 text-white font-semibold rounded-lg shadow-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default UpdateBlendedCourse;